import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const SetupProfile = () => {
    const navigate = useNavigate();
    const [displayName, setDisplayName] = useState('');
    const [role, setRole] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [authUser, setAuthUser] = useState(null);
    const [checkingAuth, setCheckingAuth] = useState(true);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => {
            if (!user) {
                // Not authenticated, redirect to login
                navigate('/login', { replace: true });
                return;
            }
            setAuthUser(user);
            setCheckingAuth(false);
        });
        return () => unsub();
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!displayName.trim()) {
            setError('Please enter a display name');
            return;
        }
        
        if (!role) {
            setError('Please select a role');
            return;
        }

        if (!authUser) {
            setError('Not authenticated. Please sign in again.');
            navigate('/login');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const userRef = doc(db, 'users', authUser.uid);
            const snapshot = await getDoc(userRef);
            const isNewUser = !snapshot.exists();

            const userData = {
                uid: authUser.uid,
                email: authUser.email || '',
                displayName: displayName.trim(),
                role,
                // Also set name and nameLower for compatibility with existing code
                name: displayName.trim(),
                nameLower: displayName.trim().toLowerCase(),
            };

            if (isNewUser) {
                userData.createdAt = serverTimestamp();
                userData.followersCount = 0;
                userData.followingCount = 0;
                userData.totalTips = 0;
                userData.badges = [];
            }

            await setDoc(userRef, userData, { merge: true });
            
            console.log('Profile created successfully');
            // Redirect to home after successful save
            navigate('/', { replace: true });
        } catch (err) {
            console.error('Error creating profile:', err);
            setError(err.message || 'Failed to create profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (checkingAuth) {
        return (
            <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center">
                <div>Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold mb-2">Setup Your Profile</h1>
                    <p className="text-white/70">Complete your profile to get started</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-200 text-sm">
                            {error}
                        </div>
                    )}

                    <div>
                        <label htmlFor="displayName" className="block text-sm font-medium mb-2">
                            Display Name
                        </label>
                        <input
                            id="displayName"
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Enter your display name"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-3">
                            Role
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                type="button"
                                onClick={() => setRole('artist')}
                                className={`px-6 py-4 rounded-lg border-2 transition ${
                                    role === 'artist'
                                        ? 'border-red-500 bg-red-500/10 text-white'
                                        : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20'
                                }`}
                            >
                                <div className="text-2xl mb-2">🎤</div>
                                <div className="font-semibold">Artist</div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setRole('listener')}
                                className={`px-6 py-4 rounded-lg border-2 transition ${
                                    role === 'listener'
                                        ? 'border-red-500 bg-red-500/10 text-white'
                                        : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20'
                                }`}
                            >
                                <div className="text-2xl mb-2">👂</div>
                                <div className="font-semibold">Listener</div>
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] text-white font-semibold py-3 rounded-lg shadow-[0_8px_30px_rgba(255,0,0,0.35)] ring-1 ring-red-500/30 hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Creating Profile...' : 'Complete Setup'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default SetupProfile;

